import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { PreferredTiming } from '@/types';
import useSlotSelectionStore from '@/stores/useSlotSelectionStore';
import { useFetchPickupSlots } from '@/hooksApi';
import SlotModal from './SlotModal';

// Singleton, mandatory slot-selection modal — mounted once at the app root
// (next to ConfirmModal). Any ship flow that hits the backend's
// SLOT_SELECTION_REQUIRED gate opens this via useSlotSelectionStore instead
// of showing a toast. It never renders a way out (no close button, no mask
// dismissal, no keyboard escape) and never surfaces a same-day cutoff — the
// merchant must pick a slot to proceed. Visuals live in the shared
// <SlotModal mode='mandatory' /> shell (see SlotModal.tsx).
const SlotSelectionModal = () => {
  const { t } = useTranslation();
  const { isOpen, onSelect, closeSlotSelection } = useSlotSelectionStore();
  const [selectedCode, setSelectedCode] = useState<string | undefined>(undefined);

  const { data, isLoading } = useFetchPickupSlots(isOpen);

  useEffect(() => {
    if (!isOpen) {
      setSelectedCode(undefined);
    }
  }, [isOpen]);

  // Single-step confirm: SlotModal passes BOTH the picked slot code and the
  // chosen preferred timing bucket (null = Anytime) — forwarded onto the
  // retried ship via onSelect so the pickup request carries `time_slot_code`
  // + `preferred_timing`.
  const handleConfirm = (code?: string, preferredTiming?: PreferredTiming | null) => {
    const finalCode = code ?? selectedCode;
    if (!finalCode) return;
    onSelect(finalCode, preferredTiming);
    setSelectedCode(undefined);
    closeSlotSelection();
  };

  return (
    <SlotModal
      open={isOpen}
      mode='mandatory'
      title={String(t('select_pickup_slot_title'))}
      subtitle={String(t('slot_modal_subtitle_mandatory'))}
      slots={data?.slots ?? []}
      isLoading={isLoading}
      selectedCode={selectedCode}
      onChangeSelected={setSelectedCode}
      onConfirm={handleConfirm}
    />
  );
};

export default SlotSelectionModal;
